import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    inject,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

import { FlatButtonComponent } from '@shared/button/flat-button/flat-button.component';
import { TooltipComponent } from '@shared/tooltip/tooltip.component';
import { slideInOutAnimation } from 'app/utils/animation';

@Component({
    selector: 'app-panel',
    imports: [
        MatIconModule,
        MatButtonModule,
        FlatButtonComponent,
        TooltipComponent,
    ],
    templateUrl: './panel.component.html',
    styleUrl: './panel.component.scss',
    animations: [slideInOutAnimation],
})
export class PanelComponent implements AfterViewInit, OnChanges {
    @ViewChild('headerTitleEl', { static: false }) headerTitleRef!: ElementRef;
    @Input() headerTitle = '';
    @Input() headerPrimaryButtonLabel = '';
    @Input() headerPrimaryButtonDisabled = false;
    @Input() headerPrimaryButtonDataQa = '';
    @Output() closeClicked = new EventEmitter();
    @Output() primaryButtonClicked = new EventEmitter();

    isTitleOverflowing = false;

    private readonly _minHeight = 100;
    private _isResizing = false;
    private _resizable: HTMLElement | undefined;
    private _dragHandle: HTMLElement | undefined;

    private readonly el = inject(ElementRef);

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['headerTitle'] && !changes['headerTitle'].firstChange) {
            this.isTitleOverflowing = false;
            setTimeout(() => this.updateTitleOverflow());
        }
    }

    ngAfterViewInit(): void {
        setTimeout(() => this.updateTitleOverflow());
        this._resizable = this.el.nativeElement.querySelector('#resizable');
        this._dragHandle = this.el.nativeElement.querySelector('#drag-handle');

        this._dragHandle?.addEventListener('mousedown', () =>
            this.onMouseDown(),
        );
    }

    onCloseClick() {
        this.closeClicked.emit();
    }

    onPrimaryButtonClick() {
        this.primaryButtonClicked.emit();
    }

    private get maxHeight() {
        return innerHeight - 100;
    }

    private updateTitleOverflow() {
        const el = this.headerTitleRef?.nativeElement;
        if (!el) return;
        this.isTitleOverflowing = el.scrollWidth > el.clientWidth;
    }

    private onMouseDown() {
        this._isResizing = true;
        document.body.style.cursor = 'row-resize';
        document.body.style.userSelect = 'none';

        document.addEventListener('mousemove', this.onMouseMove);
        document.addEventListener('mouseup', this.onMouseUp);
    }

    private readonly onMouseMove = (e: MouseEvent) => {
        if (!this._isResizing) return;
        const height = Math.max(
            this._minHeight,
            Math.min(innerHeight - e.clientY, this.maxHeight),
        );
        if (this._resizable) this._resizable.style.height = `${height}px`;
    };

    private readonly onMouseUp = () => {
        this._isResizing = false;
        document.body.style.cursor = 'default';
        document.body.style.userSelect = 'auto';

        document.removeEventListener('mousemove', this.onMouseMove);
        document.removeEventListener('mouseup', this.onMouseUp);
    };
}
